import React, { useCallback, useEffect, useRef, useState } from "react";
import Filter from "./Filter";
import GridCustomPage from "ui/grid/GridPage";
import { getInvoicesApi, InvoiceFilter } from "api/accounting";
import styles from "styles.module.css";
import { useTranslation } from "react-i18next";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { InvoiceGridColumnItem } from "./GridColumns";
import { GridCellProps, GridRowProps } from "@progress/kendo-react-grid";
import { PageTypePath } from "utils/pages";
import { ICON_NAMES } from "utils/constant";
import InvoiceHeader from "./InvoiceHeader";
import { Loading } from "ui/Loading";
import { usePageAccess } from "hooks/usePageAccess";
import { Invoice as InvoiceItem } from "interface/accounting";
import InvoicesMobileList from "./InvoicesMobileList";
import "./styles.css";

const pageSize = 60;

function Invoice() {
	const { t } = useTranslation();
	const { pageAccess, loading } = usePageAccess();

	const gridRef = useRef<HTMLDivElement>(null);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<InvoiceItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const [filterOptions, setFilterOptions] = useState<InvoiceFilter>({
		take: pageSize,
		skip: 0,
		fromDate: null,
		toDate: null,
	});
	const [statusOverrides, setStatusOverrides] = useState<Record<number, number>>({});

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getInvoicesApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as InvoiceItem[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const openPdfByRow = (prop: GridCellProps) => {
		window.open(`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`, "_blank");

		if (prop.dataItem.status !== 1) {
			setStatusOverrides((prev) => ({ ...prev, [prop.dataItem.id]: 1 }));
		}

		return;
	};

	const openPdfByItem = (item: InvoiceItem) => {
		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");
	};

	if (loading) {
		return <Loading show={true} />;
	}

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	return (
		<div className={`${styles.PageContainer} invoice-page`}>
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<legend
					className={styles.BoxHeadingLarge}
					style={{ paddingBottom: "20px" }}
				>
					{t("Invoice")}
				</legend>
			</div>
			{/*<div>*/}
			{/*	<InvoiceHeader />*/}
			{/*</div>*/}
			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<InvoicesMobileList
						items={mobileData}
						loading={mobileLoading}
						onOpenPdf={openPdfByItem}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						gridColumns={InvoiceGridColumnItem(statusOverrides)}
						itemAboveHeight={200}
						handelShowRowBgColor={handelShowRowBgColor}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						lastColWidth="60px"
						apiMethod={getInvoicesApi}
						showDetailIcon={false}
						excelProps={{
							fileName: `invoices_${new Date().toISOString()}.xlsx`,
						}}
						lastColBtns={[
							{
								onclick: openPdfByRow,
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
}

export default Invoice;

const handelShowRowBgColor = (rowData: GridRowProps) => {
	const data = rowData.dataItem;
	if (data.invoiceTypeId === 5) {
		if (data.netValue < 0) {
			return "rgb(166 36 8 / 10%)";
		} else {
			return "rgb(8 166 87 / 10%)";
		}
	}
	return "";
};
